import { ActionSearchBar } from "@/components/layout/action-search-bar";
import { QUICK_LINKS } from "@/components/layout/navLinks";
import { SupportWhatsAppLink } from "@/components/support/SupportWhatsAppLink";
import { useFloatingButtonsClearance } from "@/components/support/useFloatingButtonsLift";
import { Card, CardContent } from "@/components/ui/card";
import {
  analytics,
  getPreviousPathname,
  PII_MASK_CLASS
} from "@/lib/analytics";
import { useProfile } from "@/queries/customer/customer";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, SearchX } from "lucide-react";
import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";

/**
 * The "we couldn't find that page" screen for a signed-in salon.
 *
 * Rendered inside the app shell, so the sidebar and header stay put and the
 * page's job is recovery: search for what they came for, jump to one of the
 * everyday pages, or ask us. An unmatched URL reaches it through the `$` splat
 * route (`notFoundRoute`); `layoutRoute`'s `notFoundComponent` renders it too,
 * but only for a `notFound()` thrown by a page inside the shell. The requested
 * path is shown so a mistyped address is easy to spot, and the query string and
 * fragment are left out - they can carry tokens or search terms.
 */
export function NotFoundPage() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname
  });
  const { data } = useProfile();
  const floatingButtonsClearance = useFloatingButtonsClearance();

  // Once per path, not once per render. The ref (rather than a bare effect)
  // also keeps React StrictMode's double-invoked effect in development from
  // reporting the same visit twice.
  const reportedPath = useRef<string | null>(null);
  useEffect(() => {
    if (reportedPath.current === pathname) return;
    reportedPath.current = pathname;
    const fromPath = getPreviousPathname();
    analytics.notFoundViewed({
      path: pathname,
      referrer: document.referrer,
      ...(fromPath ? { from_path: fromPath } : {})
    });
  }, [pathname]);

  return (
    // Below `md` the floating support buttons (and the sticky cart bar they
    // ride above) sit over the bottom of the scroll area, so the last card and
    // the WhatsApp line need room to scroll clear of them: the FAB stack
    // (useFloatingButtonsClearance) plus a 1rem gap.
    <div
      className="flex-1 p-4 pb-[calc(var(--not-found-floating-clearance)+1rem)] md:p-8"
      style={
        {
          "--not-found-floating-clearance": floatingButtonsClearance
        } as CSSProperties
      }
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8">
        <header className="flex flex-col items-start gap-4">
          <div
            aria-hidden="true"
            className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-pink-500"
          >
            <SearchX className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0 max-w-full">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
              We couldn&apos;t find that page
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              The link may be out of date or mistyped. Try a search, or pick up
              where you left off below.
            </p>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="sr-only">Address you asked for: </span>
              <code
                className={`inline-block max-w-full break-all rounded bg-gray-200 px-1.5 py-0.5 font-mono dark:bg-neutral-900 ${PII_MASK_CLASS}`}
              >
                {pathname}
              </code>
            </p>
          </div>
        </header>

        <section aria-labelledby="not-found-search" className="max-w-xl">
          <h2
            id="not-found-search"
            className="mb-2 text-sm font-semibold text-gray-900 dark:text-white"
          >
            Search for a product
          </h2>
          <ActionSearchBar />
        </section>

        <nav aria-labelledby="not-found-links">
          <h2
            id="not-found-links"
            className="mb-3 text-sm font-semibold text-gray-900 dark:text-white"
          >
            Or jump back in
          </h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {QUICK_LINKS.map(({ label, to, Icon }) => (
              <li key={label}>
                <Link
                  to={to}
                  className="group block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 dark:focus-visible:ring-purple-400 dark:focus-visible:ring-offset-neutral-800"
                >
                  <Card className="transition-colors group-hover:border-purple-300 dark:group-hover:border-purple-700">
                    <CardContent className="flex items-center gap-3 p-4">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
                        <Icon
                          className="h-5 w-5 text-purple-600 dark:text-purple-400"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                        {label}
                      </span>
                      <ChevronRight
                        className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          Still stuck?{" "}
          <SupportWhatsAppLink
            profile={data?.customer}
            className="font-medium text-purple-600 underline-offset-2 hover:underline focus-visible:underline dark:text-purple-400"
          >
            Message us on WhatsApp
          </SupportWhatsAppLink>
        </p>
      </div>
    </div>
  );
}
